import Vue from "vue";
import Vuex from "vuex";
import Vuei18n from "@/i18n";
import { MutationTree, ActionTree, GetterTree, Action, Commit } from "vuex";
import { auth, db, googleAuthProvider, facebookAuthProvider } from "@/firebaseConfig";
import { UserSignUpInput, WishifyUser } from "@/models/wishify-user";
import { Alert, AlertType } from "@/models/alert.ts";
import { MUTATIONS, ACTIONS } from "@/models/vuex-types";
import { UserCredential, User } from "@firebase/auth-types";
import { FirestoreError } from "@firebase/firestore-types";
import { UserProfile } from "@/models/profile";

Vue.use(Vuex);

interface IState {
  locale: string;
  alerts: Alert[];
  currentUser: User | null;
  userProfile: UserProfile;
  isPageLoaded: boolean;
  uiIsBlocked: boolean;
}

const state: IState = {
  locale: "cs",
  alerts: [],
  currentUser: null,
  userProfile: new UserProfile(),
  isPageLoaded: false,
  uiIsBlocked: false,
};

const getters: GetterTree<IState, any> = {
  getAlertFromError(state: IState, getters: any): any {
    return (firebaseError: FirestoreError) => {
      // tslint:disable-next-line:no-console
      console.error(firebaseError);

      const newAlert = new Alert(AlertType.ERROR);

      // if(process.env.NODE_ENV !== "production") {

      // }

      if (firebaseError && firebaseError.code !== undefined) {
        newAlert.message = Vuei18n.te(firebaseError.code) ? Vuei18n.t(firebaseError.code).toString() : firebaseError.message;
      } else {
        newAlert.message = Vuei18n.t("default_error").toString();
      }
      return newAlert;
    };
  },
};

const mutations: MutationTree<IState> = {
  [MUTATIONS.SET_LOCALE](state: IState, locale: string): void {
    state.locale = locale;
  },
  [MUTATIONS.ADD_ALERT](state: IState, alert: Alert): void {
    state.alerts.unshift(alert);
  },
  [MUTATIONS.CLEAR_ALERT](state: IState, id: string): void {
    let alertToDelete = state.alerts.find( (alert) => alert.id === id );
    state.alerts.splice(state.alerts.indexOf(alertToDelete), 1);
  },
  [MUTATIONS.SET_CURRENT_USER](state: IState, user: User): void {
    state.currentUser = user;
  },
  [MUTATIONS.SET_UI_BLOCK](state: IState, uiIsBlocked: boolean): void {
    state.uiIsBlocked = uiIsBlocked;
  },
  [MUTATIONS.SET_IS_PAGE_LOADED](state: IState, isPageLoaded: boolean): void {
    state.isPageLoaded = isPageLoaded;
  },
  [MUTATIONS.SET_USERPROFILE](state: IState, userProfile: UserProfile): void {
    state.userProfile = userProfile;
  },
};

const actions: ActionTree<IState, IState> = {
  async [ACTIONS.SIGN_UP_USER]( { commit, getters, dispatch } , userSignUpInput: UserSignUpInput): Promise<UserCredential> {
    let userCredential: UserCredential;
    try {
      userCredential = await auth.createUserWithEmailAndPassword(userSignUpInput.email, userSignUpInput.password);
    } catch (error) {
      commit(MUTATIONS.ADD_ALERT, getters.getAlertFromError(error));
    }
    if (userCredential) {
      try {
        await userCredential.user.updateProfile({ displayName: userSignUpInput.name, photoURL: "" });
      } catch (error) {
        commit(MUTATIONS.ADD_ALERT, getters.getAlertFromError(error));
      }
      let newUser: WishifyUser = userCredential.user;
      commit(MUTATIONS.SET_CURRENT_USER, newUser);
      commit(MUTATIONS.ADD_ALERT, new Alert(AlertType.SUCCESS, Vuei18n.t("sign_up_success").toString()));
      commit(MUTATIONS.ADD_ALERT, new Alert(AlertType.SUCCESS, Vuei18n.t("sign_in_success").toString()));

      userCredential.user.sendEmailVerification({url: window.location.origin + "/profile"});
    }
    return userCredential;
  },
  async [ACTIONS.SIGN_IN_USER]({ commit, getters, dispatch }, userSignUpInput: UserSignUpInput): Promise<UserCredential> {
    auth.languageCode = state.locale;
    let userCredential: any =
      await auth.signInWithEmailAndPassword(userSignUpInput.email, userSignUpInput.password).then( (response) => {
        return response;
      }).catch( (error) => {
        commit(MUTATIONS.ADD_ALERT, getters.getAlertFromError(error));
      });
    if (userCredential) {
      let newUser: WishifyUser = userCredential.user;
      commit(MUTATIONS.SET_CURRENT_USER, newUser);
      commit(MUTATIONS.ADD_ALERT, new Alert(AlertType.SUCCESS, Vuei18n.t("sign_in_success").toString()));
    }
    return userCredential;
  },
  [ACTIONS.SIGN_IN_USER_WITH_GOOGLE]({ state, commit, getters, dispatch }, userSignUpInput: UserSignUpInput): void {
    auth.languageCode = state.locale;
    auth.signInWithRedirect(googleAuthProvider);
  },
  [ACTIONS.SIGN_IN_USER_WITH_FACEBOOK]({ state, commit, getters, dispatch }, userSignUpInput: UserSignUpInput): void {
    auth.languageCode = state.locale;
    auth.signInWithRedirect(facebookAuthProvider);
  },
  async [ACTIONS.SIGN_OUT_USER]({ commit, getters, dispatch }): Promise<boolean> {
    let signedOutSuccesfully = false;
    await auth.signOut().then((response) => {
      commit(MUTATIONS.SET_CURRENT_USER, null);
      // commit(MUTATIONS.ADD_ALERT, new Alert(AlertType.INFO, Vuei18n.t("sign_out_success").toString()));
      signedOutSuccesfully = true;
    }).catch( (error) => {
      commit(MUTATIONS.ADD_ALERT, getters.getAlertFromError(error));
    });
    return signedOutSuccesfully;
  },
};

export default new Vuex.Store<IState>({
  state,
  getters,
  mutations,
  actions,
});
